import {
  collection,
  getDocs,
  addDoc,
  updateDoc,
  deleteDoc,
  doc,
  query,
  orderBy,
  where,
  getDoc,
  writeBatch,
  Timestamp
} from 'firebase/firestore';
import { db } from '../database/firebase';
import { logInwardTransaction } from './transactionService';

/**
 * Fetch all purchases from Firestore
 * @returns {Promise<Array>} Array of purchase objects
 */
export const getAllPurchases = async () => {
  try {
    const purchasesRef = collection(db, 'purchaseDetails');
    const q = query(purchasesRef, orderBy('createdAt', 'desc'));
    const querySnapshot = await getDocs(q);

    const purchases = [];
    querySnapshot.forEach((doc) => {
      purchases.push({
        id: doc.id,
        ...doc.data(),
      });
    });

    return purchases;
  } catch (error) {
    console.error('Error fetching purchases:', error);
    throw error;
  }
};

/**
 * Get a single purchase by ID
 * @param {string} purchaseId - Purchase document ID
 * @returns {Promise<Object|null>} Purchase object or null
 */
export const getPurchaseById = async (purchaseId) => {
  try {
    const purchaseRef = doc(db, 'purchaseDetails', purchaseId);
    const purchaseSnap = await getDoc(purchaseRef);

    if (purchaseSnap.exists()) {
      return {
        id: purchaseSnap.id,
        ...purchaseSnap.data(),
      };
    } else {
      return null;
    }
  } catch (error) {
    console.error('Error fetching purchase by ID:', error);
    throw error;
  }
};

/**
 * Add a new purchase and update product quantities
 * @param {Object} purchaseData - Purchase data
 * @returns {Promise<string>} Document ID of the created purchase
 */
export const addPurchase = async (purchaseData) => {
  try {
    const batch = writeBatch(db);

    // Add purchase document
    const purchaseRef = doc(collection(db, 'purchaseDetails'));
    batch.set(purchaseRef, {
      ...purchaseData,
      status: 'completed',
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
    });

    // Update product quantities if inWard is true
    if (purchaseData.items && Array.isArray(purchaseData.items)) {
      for (const item of purchaseData.items) {
        if (item.inWard) {
          const productRef = doc(db, 'products', item.productId);
          const productSnap = await getDoc(productRef);

          if (productSnap.exists()) {
            const currentStock = productSnap.data().stockCount || 0;
            const newStock = Number(currentStock) + Number(item.quantity);

            batch.update(productRef, {
              stockCount: newStock,
              updatedAt: new Date().toISOString(),
            });
          }
        }
      }
    }

    await batch.commit();

    // Log inward transaction for inventory tracking
    // Only log items that are marked as inWard
    const inwardItems = purchaseData.items?.filter(item => item.inWard) || [];

    if (inwardItems.length > 0) {
      try {
        await logInwardTransaction({
          date: Timestamp.fromDate(new Date(purchaseData.purchaseDate)),
          userName: purchaseData.supplierName || 'Unknown Supplier',
          userId: purchaseData.supplierId || 'unknown',
          products: inwardItems.map(item => ({
            productId: item.productId,
            productCode: item.productCode,
            productName: item.productName,
            quantity: item.quantity,
            uom: item.uom,
            rate: item.purchaseRate,
            price: item.purchaseRate,
            amount: item.amount,
            valuePoints: item.valuePoint || item.volumePoints || 0,
          })),
          totalAmount: inwardItems.reduce((sum, item) => sum + (item.amount || 0), 0),
          totalValuePoints: inwardItems.reduce((sum, item) => sum + ((item.volumePoints || 0) * (item.quantity || 0)), 0),
          createdBy: 'purchase-system',
        });
      } catch (transactionError) {
        console.error('Error logging inward transaction:', transactionError);
        // Don't throw - purchase was successful even if transaction logging failed
      }
    }

    return purchaseRef.id;
  } catch (error) {
    console.error('Error adding purchase:', error);
    throw error;
  }
};

/**
 * Update a purchase in Firestore
 * @param {string} purchaseId - Purchase document ID
 * @param {Object} purchaseData - Updated purchase data
 * @returns {Promise<void>}
 */
export const updatePurchase = async (purchaseId, purchaseData) => {
  try {
    const purchaseRef = doc(db, 'purchaseDetails', purchaseId);
    await updateDoc(purchaseRef, {
      ...purchaseData,
      updatedAt: new Date().toISOString(),
    });
  } catch (error) {
    console.error('Error updating purchase:', error);
    throw error;
  }
};

/**
 * Delete a purchase from Firestore
 * @param {string} purchaseId - Purchase document ID
 * @returns {Promise<void>}
 */
export const deletePurchase = async (purchaseId) => {
  try {
    const purchaseRef = doc(db, 'purchaseDetails', purchaseId);
    await deleteDoc(purchaseRef);
  } catch (error) {
    console.error('Error deleting purchase:', error);
    throw error;
  }
};

/**
 * Get purchases by date range
 * @param {string} startDate - Start date (ISO string)
 * @param {string} endDate - End date (ISO string)
 * @returns {Promise<Array>} Array of purchase objects
 */
export const getPurchasesByDateRange = async (startDate, endDate) => {
  try {
    const purchasesRef = collection(db, 'purchaseDetails');
    const q = query(
      purchasesRef,
      where('purchaseDate', '>=', startDate),
      where('purchaseDate', '<=', endDate),
      orderBy('purchaseDate', 'desc')
    );
    const querySnapshot = await getDocs(q);

    const purchases = [];
    querySnapshot.forEach((doc) => {
      purchases.push({
        id: doc.id,
        ...doc.data(),
      });
    });

    return purchases;
  } catch (error) {
    console.error('Error fetching purchases by date range:', error);
    throw error;
  }
};

/**
 * Get purchases by supplier
 * @param {string} supplierId - Supplier user ID
 * @returns {Promise<Array>} Array of purchase objects
 */
export const getPurchasesBySupplier = async (supplierId) => {
  try {
    const purchasesRef = collection(db, 'purchaseDetails');
    const q = query(
      purchasesRef,
      where('supplierId', '==', supplierId),
      orderBy('createdAt', 'desc')
    );
    const querySnapshot = await getDocs(q);

    const purchases = [];
    querySnapshot.forEach((doc) => {
      purchases.push({
        id: doc.id,
        ...doc.data(),
      });
    });

    return purchases;
  } catch (error) {
    console.error('Error fetching purchases by supplier:', error);
    throw error;
  }
};

/**
 * Get purchase statistics
 * @returns {Promise<Object>} Purchase statistics
 */
export const getPurchaseStatistics = async () => {
  try {
    const purchasesRef = collection(db, 'purchaseDetails');
    const querySnapshot = await getDocs(purchasesRef);

    let totalAmount = 0;
    let totalVolumePoints = 0;
    let totalPurchases = 0;

    querySnapshot.forEach((doc) => {
      const data = doc.data();
      totalAmount += Number(data.totalAmount || 0);
      totalVolumePoints += Number(data.totalVolumePoints || 0);
      totalPurchases += 1;
    });

    return {
      totalAmount,
      totalVolumePoints,
      totalPurchases,
    };
  } catch (error) {
    console.error('Error fetching purchase statistics:', error);
    throw error;
  }
};

/**
 * Get owners/suppliers (users with owner = true)
 * @returns {Promise<Array>} Array of owner/supplier users
 */
export const getSuppliers = async () => {
  try {
    const usersRef = collection(db, 'users');
    const q = query(usersRef, where('owner', '==', true));
    const querySnapshot = await getDocs(q);

    const suppliers = [];
    querySnapshot.forEach((doc) => {
      suppliers.push({
        id: doc.id,
        ...doc.data(),
      });
    });

    return suppliers;
  } catch (error) {
    console.error('Error fetching suppliers:', error);
    throw error;
  }
};
